import type { JSX } from "react"

import { cn } from "@/lib/utils"
import type { UesFieldDef } from "@/pages/doc-type-management/domain/entities/doc-type"

export interface UesTableProps {
  fields: Record<string, unknown>
  defs?: UesFieldDef[]
}

type UesLeaf = {
  value: unknown
  confidence?: number
}

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false
  }
  const prototype = Object.getPrototypeOf(value)
  return prototype === Object.prototype || prototype === null
}

export function isLeaf(value: unknown): value is UesLeaf {
  if (!isPlainObject(value) || !Object.hasOwn(value, "value")) return false
  const keys = Object.keys(value)
  if (keys.some((key) => key !== "value" && key !== "confidence")) return false
  return (
    !Object.hasOwn(value, "confidence") || typeof value.confidence === "number"
  )
}

const isEmpty = (value: unknown): boolean =>
  value === null ||
  value === undefined ||
  (typeof value === "string" && value.trim() === "")

// Threshold bands (spec): >=90% green, 60–89% amber, <60% red.
const confidenceClass = (pct: number): string => {
  if (pct >= 90) {
    return "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200"
  }
  if (pct >= 60) {
    return "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200"
  }
  return "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200"
}

const ConfidenceTag = ({ confidence }: { confidence: number }): JSX.Element => {
  const pct = Math.round(confidence * 100)
  return (
    <span
      className={cn(
        "mt-1 inline-flex w-fit items-center rounded px-1.5 py-0.5 text-xs font-medium",
        confidenceClass(pct),
      )}
      title="Extraction confidence"
    >
      {pct}%
    </span>
  )
}

const EmptyValue = (): JSX.Element => (
  <span className="text-muted-foreground">---</span>
)

// Defensive: `ues.fields` is dict[str, Any] — a leaf may or may not be the
// {value, confidence} shape, values may be arrays or nested objects. Never
// assume the shape.
function UesValue({
  value,
  def,
}: {
  value: unknown
  def?: UesFieldDef
}): JSX.Element {
  if (Array.isArray(value)) {
    if (value.length === 0) return <EmptyValue />
    return (
      <div className="space-y-2">
        {value.map((item, index) => (
          <div key={index} className="rounded-md border border-border/60 p-2">
            <UesValue value={item} def={def} />
          </div>
        ))}
      </div>
    )
  }

  if (isLeaf(value)) {
    if (isEmpty(value.value)) return <EmptyValue />
    return (
      <div className="flex flex-col">
        <span className="break-words">{String(value.value)}</span>
        {typeof value.confidence === "number" && (
          <ConfidenceTag confidence={value.confidence} />
        )}
      </div>
    )
  }

  if (isPlainObject(value)) {
    const entries = Object.entries(value)
    if (entries.length === 0) return <EmptyValue />
    return (
      <dl className="space-y-1.5">
        {entries.map(([key, child]) => {
          const childDef = def?.fields?.find((field) => field.name === key)
          return (
            <div
              key={key}
              className="grid gap-0.5 sm:grid-cols-[10rem_1fr] sm:gap-x-3"
            >
              <dt className="text-xs text-muted-foreground">
                {childDef?.label || key}
              </dt>
              <dd className="min-w-0">
                <UesValue value={child} def={childDef} />
              </dd>
            </div>
          )
        })}
      </dl>
    )
  }

  if (isEmpty(value)) return <EmptyValue />
  return <span className="break-words">{String(value)}</span>
}

export function UesTable({ fields, defs }: UesTableProps): JSX.Element {
  // Prefer the doc-type schema for row order + labels so empty fields still
  // render a "---" row; fall back to the extracted keys when defs are absent
  // (e.g. the source doc type was deleted).
  const rows =
    defs && defs.length > 0
      ? defs.map((def) => ({
          name: def.name,
          label: def.label || def.name,
          def: def as UesFieldDef | undefined,
        }))
      : Object.keys(fields).map((name) => ({
          name,
          label: name,
          def: undefined as UesFieldDef | undefined,
        }))

  if (rows.length === 0) {
    return <p className="text-muted-foreground">No fields.</p>
  }

  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <table className="w-full border-collapse text-left text-sm">
        <colgroup>
          <col className="w-1/3" />
          <col className="w-2/3" />
        </colgroup>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.name}
              className="border-b border-border align-top last:border-b-0"
            >
              <td className="break-words px-3 py-2 font-medium text-muted-foreground">
                {row.label}
              </td>
              <td className="min-w-0 px-3 py-2">
                <UesValue value={fields[row.name]} def={row.def} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
